// How well the opponent plays.
//
// A weak opponent is *not* a random one: a bot that shuffles pieces at random
// looks broken rather than beatable, and it teaches nothing, because the
// player never finds out that a hanging piece gets taken. So easy mode keeps
// the same evaluation and degrades the two things a beginner is actually bad
// at — seeing the reply, and telling near-equal moves apart.
//
// Both knobs are deterministic in use (see `streamFor`): the rolls come from
// the game's seed and its ply count, so a replayed log makes the same
// "mistakes" in the same places. An opponent that varied between replays would
// desync a reloaded or spectated game exactly like unseeded randomness in
// `src/engine/rules/`.

import { RULES } from "src/engine/rules/rules";

export type Level = "easy" | "normal" | "hard";

/**
 * How far into the run the *difficulty* has got, which is not the same as how
 * many fronts have been cleared.
 *
 * Everything that ramps used to read the front number directly — the camp
 * tier, the rank a camp's pawns come back with, the level the opponent plays
 * at, the front that may open on two sides — so clearing a front advanced all
 * four of them a whole step. That is a curve nobody chose: it is whatever
 * falls out of counting, and it climbs at exactly the rate the board grows.
 *
 * `RULES.stagePace` is the rate, and this is the one place it is applied.
 * Anything asking "how hard should this front be" asks this; anything asking
 * "which front is this" — the chip, the save, the seeded streams, the tally —
 * keeps the count, because the two questions genuinely differ and the run
 * needs both.
 *
 * The first front is exactly the first front, whatever the pace: a run opens
 * on the board it was designed to open on.
 */
export function pacedStage(stage: number): number {
  return Math.max(1, Math.floor((stage - 1) * RULES.stagePace) + 1);
}

export interface Skill {
  /**
   * Chance per turn that it plays without looking at the opponent's reply at
   * all — the turn it hangs something. This is the blunder that reads as a
   * blunder, because the piece is simply there to be taken.
   */
  blindRate: number;
  /**
   * Centipawns of noise added to every candidate move, ±. Deliberately smaller
   * than a pawn: it is there to scramble the order of near-equal quiet moves,
   * not to argue with material. At ±160 it did argue with material — thirty
   * candidates means the highest draw is near the top of the range, so the
   * move chosen was whichever one got lucky and easy mode played nearly at
   * random, which reads as broken rather than beatable. Blindness is the
   * blunder; this is only sloppiness.
   */
  noise: number;
  /**
   * The heaviest camp a wave may reach for. Easy keeps to the cheap end, so
   * beating one stage does not immediately hand you a harder one than the one
   * you just won.
   *
   * It has to reach the top of `FORMATIONS`, or the heavy camps are written
   * and never seen — which is exactly what happened when the table grew past
   * the number here and nothing moved it. Hard reaches everything; easy stops
   * short of the siege.
   */
  reinforcementTiers: number;
  /**
   * How far beyond its own pieces it bothers to think, in squares.
   *
   * The board stops being eight-by-eight in a run and keeps growing, and the
   * search is quadratic in the wrong place — one `scoreMove` clones the
   * position and generates every reply, which sweeps the whole board. Thirty
   * candidates on a four-hundred-square map is thirty full-board sweeps for a
   * turn whose interesting moves are all within a few squares of its own
   * army.
   *
   * So it plans inside a window around its own pieces and ignores the rest.
   * That is a *simplification*, not a handicap it has to be told about: an
   * army that reasons about the ground it is standing on and not about a
   * frontier forty squares away is what a person does too. It is per-skill
   * because a weaker opponent seeing less far is the same idea as a weaker
   * opponent missing the reply.
   */
  horizon: number;
  /**
   * Centipawns per square closed toward the nearest enemy, on a quiet move.
   *
   * The knob that decides whether the opponent *comes for you*. It has to sit
   * clearly above `noise` or the noise decides every quiet move and the army
   * reads as shuffling — which is what it did, because the terms this replaced
   * were worth about two points each against noise of seventy.
   *
   * It is a pull, not a rule: a hanging piece is still worth more than three
   * squares of ground, so an opponent that can win material still does.
   */
  aggression: number;
  /**
   * Centipawns per square closed toward *factory work*, on a quiet move.
   *
   * `aggression`'s twin, and the answer to the same complaint from the other
   * side. Watching two machines play showed a hundred and eight plies that
   * produced three belt parts and forty-three units sitting in buffers that
   * never became anything: the opponent mined what it happened to be standing
   * on, and every move it chose was a chess move. It had no way to *decide*
   * that walking a pawn onto a scrap heap was worth more than walking it
   * toward the enemy.
   *
   * This is that decision, priced in the same units as everything else it
   * weighs, so the two pulls argue rather than one of them being a rule. Three
   * things it pays for: standing a harvester on scrap, closing on scrap worth
   * working, and carrying a load to somewhere it can be put down.
   *
   * Deliberately near `aggression` rather than above it. An opponent that
   * always preferred the mine would stop playing chess, which is the same
   * failure as an opponent that always marches — and the factory is supposed
   * to be what you do *with* the war, not instead of it.
   */
  greed: number;
  /**
   * Plan the turn under several stances and pick between the results.
   *
   * A turn is one thing — a move and a forge both spend it, and a piece that
   * walks to a lane is a piece that did not walk at the enemy — so no single
   * weighting of the two knobs can express the choice. Whichever term bids
   * highest takes every turn. Planning as a fighter, as a worker and as
   * configured, then asking what the board is worth afterwards, is what turns
   * that into a decision.
   *
   * Off for the beginner's opponent, and not to save the work: choosing well
   * between three coherent turns is most of what makes a strong opponent
   * strong, and an opponent that judges carefully while blind to your reply
   * half the time is a strange thing to be.
   */
  stances: boolean;
  /**
   * How close you have to get before this army starts playing, in squares.
   *
   * A camp on the far side of a board that keeps growing is scenery until you
   * reach it, and an army that spends those turns building a factory and
   * marching at you is a beginner's first encounter that arrives already
   * ahead. So a dormant camp **patrols** — its pieces move, so it reads as
   * alive rather than as a painted backdrop — and does no industry and no
   * marching until somebody comes within this many squares.
   *
   * Undefined means "always playing", which is every difficulty above easy and
   * every game that is not a run. Easy has it, and `levelForStage` opens every
   * run at easy whatever was picked, so the *first* camp idles for everyone
   * and the rest of a normal or hard run does not.
   */
  wakesAt?: number;
}

/**
 * What a player may turn by hand, on top of a difficulty.
 *
 * The two knobs worth exposing: `aggression` decides whether the opponent
 * comes for you and `greed` decides whether it plays the factory, and they are
 * the pair whose balance the whole watched game turns on. Everything else in a
 * `Skill` is about how *well* it plays — blunder rate, noise, how far it looks
 * — which is what the difficulty is for.
 *
 * Optional per field, so "leave it as the difficulty has it" is expressible.
 */
export interface Tuning {
  aggression?: number;
  greed?: number;
}

/** The widest either knob goes. Past this one term decides every quiet move. */
export const TUNING_MAX = 60;

/**
 * A difficulty with any hand-set knobs applied.
 *
 * Clamped rather than trusted: these come off a slider today and out of a URL
 * or a saved setting tomorrow, and a negative aggression is an opponent that
 * runs away — which is a thing somebody will eventually want, but not by
 * accident.
 */
export function skillFor(level: Level, tuning?: Tuning): Skill {
  const base = SKILL[level];
  if (!tuning) return base;
  const clamp = (value: number | undefined, fallback: number): number =>
    value === undefined || !Number.isFinite(value)
      ? fallback
      : Math.max(0, Math.min(TUNING_MAX, value));
  return {
    ...base,
    aggression: clamp(tuning.aggression, base.aggression),
    greed: clamp(tuning.greed, base.greed)
  };
}

/**
 * How well the opponent plays at a given stage of a run, given the difficulty
 * the player asked for.
 *
 * **A run starts easy whatever you picked.** The first camp is the one where
 * you have a king, a few heaps and no idea what a refinery is for, and meeting
 * a full-strength opponent there is not a difficulty setting, it is a wall. So
 * the chosen level is a *ceiling* the run climbs to over its first few stages
 * rather than a switch thrown at the start.
 *
 * Easy stays easy throughout: somebody who picked it has said what they want.
 *
 * The stage handed in is the front number and the thresholds are in *paced*
 * stages, so how many fronts each rung takes is `RULES.stagePace`'s to say —
 * the shape of the climb is the design and its speed is a knob.
 */
export function levelForStage(chosen: Level, stage: number): Level {
  if (chosen === "easy") return "easy";
  const paced = pacedStage(stage);
  if (paced <= 1) return "easy";
  if (chosen === "normal") return "normal";
  return paced <= 3 ? "normal" : "hard";
}

export const SKILL: Record<Level, Skill> = {
  // Unchanged from before difficulty existed: the full evaluation, no noise,
  // ties broken by move order.
  hard: {
    blindRate: 0,
    noise: 0,
    reinforcementTiers: 7,
    horizon: 8,
    aggression: 26,
    greed: 20,
    stances: true
  },
  // Noise below the aggression term on purpose: a beginner picks the wrong
  // *target*, they do not wander. Blindness is the blunder; this is only
  // sloppiness, and it must not out-argue "walk toward the enemy".
  /**
   * The middle, and the one the game is tuned against.
   *
   * A beginner's opponent is a bad thing to balance an economy on — half its
   * turns are played without looking, so what you are watching is as much
   * blunder as behaviour — and the hard one gives away nothing. This is the
   * setting the industrial half is measured at: it sees enough to play a
   * reasonable game and blunders rarely enough that what it does with a lane
   * is a choice rather than noise.
   */
  normal: {
    blindRate: 0.15,
    noise: 18,
    reinforcementTiers: 6,
    horizon: 7,
    aggression: 23,
    greed: 19,
    stances: true
  },
  easy: {
    blindRate: 0.45,
    noise: 40,
    reinforcementTiers: 5,
    horizon: 5,
    aggression: 18,
    // A beginner does notice the free material on the floor — greed is the
    // easiest thing in this game to understand. What they miss is the reply.
    greed: 16,
    stances: false,
    // Six, which is a little past the horizon it plans inside: a camp wakes up
    // when you enter the ground it can actually think about, rather than at an
    // arbitrary distance that would read as it having seen you through a wall.
    wakesAt: 6
  }
};

export const LEVELS: { id: Level; name: string; detail: string }[] = [
  {
    id: "easy",
    name: "Easy",
    detail:
      "Comes for you, but misses your reply about half the time and hangs pieces. Its waves arrive light."
  },
  {
    id: "normal",
    name: "Normal",
    detail:
      "Looks at your reply most of the time and plays the factory as hard as it plays the board. The one the rest is tuned against."
  },
  {
    id: "hard",
    name: "Hard",
    detail: "Checks what you can take back on every move, and values what your pieces are carrying. Waves escalate."
  }
];
